import React from 'react';
import { Device } from '../../lib/types';
import { formatDate } from '../../lib/utils';
import DeviceCell from './DeviceCell';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead, { type TableColumn } from './TableHead';
import PetNameCell from './PetNameCell';
import styles from './Devices.module.scss';

// The trailing column holds a checkmark, not an action, but it sits where Pending's and
// Stalled's Action columns sit — so it takes the same width and the three line up.
export const COMPLETED_COLUMNS: TableColumn[] = [
  { label: 'New Owner', width: '27%' },
  { label: 'Pet', width: '18%' },
  { label: 'Device', width: '21%' },
  { label: 'Completed', width: '21%' },
  { label: 'Adopted', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
];

interface CompletedTransfersTableProps {
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  skeletonCount: number;
}

export default function CompletedTransfersTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  loading,
  skeletonCount,
}: CompletedTransfersTableProps) {
  if (totalCount === 0 && !loading && skeletonCount === 0) {
    return null;
  }

  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  return (
    <CollapsibleSection
      title="Completed"
      count={totalCount}
      variant="success"
      description="Devices successfully transferred to adopters."
    >
      <div className={wrapperClass}>
        <table className={styles.table}>
          <TableHead columns={COMPLETED_COLUMNS} />
          <tbody>
            {showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={COMPLETED_COLUMNS.length} />
            ) : (
              devices.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.adopterName}
                    {d.adopterEmail && (
                      <>
                        <br />
                        <span className={styles.adopterEmail}>{d.adopterEmail}</span>
                      </>
                    )}
                  </td>
                  <td>{d.petName ? <PetNameCell name={d.petName} photoThumbUrl={d.petPhotoThumbUrl} /> : '\u2014'}</td>
                  <td>
                    <DeviceCell moduleId={d.id} type={d.type} />
                  </td>
                  <td>{formatDate(d.completedDate)}</td>
                  <td>
                    <span className={styles.checkmark}>&#10003;</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
